import {
  IElement,
  OneOfElements,
  OneOfPropertyElements,
} from '@shared/models/project.model';
import { IElementExpressionProperty } from '@shared/models/element-property.model';
import {
  getElementProperties,
  isElementExpressionProperty,
} from '../../element-property.helpers';
import { createEmptyExpression } from '../../expression-factory.helpers';
import { projectGuards } from '../../guards/project-guards.helpers';
import { isOneOfPropertyElements } from '../../recursive-element.helpers';
import { guards } from '../../typeguards.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { ElementQuantityName } from '@shared/models/element-quantities.model';

type PropertyValueChange =
  ProjectChangeMap[ProjectChangeType.ElementPropertyValue];

/**
 * Build a partial element change for ElementPropertyValue (user override on expression count).
 *
 * @param element - Current element state
 * @param change - Override value payload ({@link ExpressionValue.value})
 * @returns Partial<OneOfElements> or undefined
 */
export const elementPropertyValueToChange = (
  element: OneOfElements,
  change: PropertyValueChange,
): Partial<OneOfElements> | undefined => {
  if (!isOneOfPropertyElements(element)) return undefined;

  const propChange = tryRegularPropertyValue(element, change);
  if (propChange) return propChange;

  if (!projectGuards.is.element(element)) return undefined;
  return tryQuantityPropertyValue(element, change);
};

const tryRegularPropertyValue = (
  element: OneOfPropertyElements,
  change: PropertyValueChange,
): Partial<OneOfElements> | undefined => {
  const props = getElementProperties(element);
  const idx = props.findIndex(
    (p) => isElementExpressionProperty(p) && p.name === change.propertyName,
  );
  if (idx === -1) return undefined;

  const prop = props[idx] as IElementExpressionProperty | undefined;
  if (!prop) return undefined;

  const base = prop.count ?? prop.fallbackCount ?? createEmptyExpression();
  if (guards.is.object(base) && base.value === change.overrideValue)
    return undefined;

  const newProps = [...props];
  newProps[idx] = {
    ...prop,
    count: { ...base, value: change.overrideValue },
  };

  return { properties: newProps };
};

const tryQuantityPropertyValue = (
  element: IElement,
  change: PropertyValueChange,
): Partial<OneOfElements> | undefined => {
  if (!element.quantity) return undefined;

  const quantityName = change.propertyName as ElementQuantityName;
  const quantity = element.quantity[quantityName];
  if (!quantity) return undefined;

  const countField =
    'count' in quantity && quantity.count
      ? 'count'
      : 'fallbackCount' in quantity && quantity.fallbackCount
        ? 'fallbackCount'
        : undefined;
  if (!countField) return undefined;

  const existing = quantity[countField as keyof typeof quantity];
  if (!guards.is.object(existing)) return undefined;

  if (existing.value === change.overrideValue) return undefined;

  return {
    quantity: {
      ...element.quantity,
      [quantityName]: {
        ...quantity,
        [countField]: { ...existing, value: change.overrideValue },
      },
    },
  };
};
